// Корень приложения: разводит экран входа и рабочее окно, держит общие подписки.

import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { setToken } from './api'
import { useAppStore } from './store/appStore'
import type { LinkState } from './store/appStore'
import { setConnSink } from './store/logStore'
import type { ConnState } from './store/logStore'
import Login from './shell/Login'
import Shell from './shell/Shell'
import MobileApp from './mobile/MobileApp'

// Стенд общих компонентов (/?ui=kit) — отдельным чанком, в основной бандл не входит.
const KitDemo = lazy(() => import('./ui/KitDemo'))
import { MOBILE_QUERY, useMediaQuery } from './lib/useMediaQuery'
import { registerServiceWorker, watchInstallPrompt } from './lib/pwa'
import { useViewportHeight } from './lib/useViewportHeight'

/** Экран загрузки с аварийным выходом.
 *
 *  Запросы теперь с таймаутом, так что зависнуть здесь не должно. Но экран,
 *  из которого нет выхода вообще, — плохая идея на телефоне: если что-то
 *  снова застрянет, человек должен уметь выйти сам, а не сносить приложение.
 */
function Boot() {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 8000)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <div className="ab-boot">
      <span className="ab-boot__dot" />
      подключение к серверу…
      {slow ? (
        <button
          className="ab-boot__escape"
          onClick={() => {
            setToken(null)
            location.reload()
          }}
        >
          долго — войти заново
        </button>
      ) : null}
    </div>
  )
}

export default function App() {
  const booting = useAppStore((s) => s.booting)
  const authed = useAppStore((s) => s.authed)
  const bootstrap = useAppStore((s) => s.bootstrap)
  const refreshSessions = useAppStore((s) => s.refreshSessions)
  const section = useAppStore((s) => s.section)
  const refreshTree = useAppStore((s) => s.refreshTree)
  const refreshFeed = useAppStore((s) => s.refreshFeed)
  const refreshTrackerNew = useAppStore((s) => s.refreshTrackerNew)
  // Старый телефонный MobileApp — только по ?ui=legacy-mobile на время
  // обкатки (docs/ui-plan.md, решение 6). Новая оболочка адаптивна для всех ширин.
  const mobile = useMediaQuery(MOBILE_QUERY)
  const legacyMobile = useLegacyMobile()

  // Высота по видимой части экрана: иначе клавиатура на телефоне закрывает ввод.
  useViewportHeight()

  // StrictMode в dev монтирует эффекты дважды — старт делаем один раз.
  const booted = useRef(false)
  useEffect(() => {
    if (booted.current) return
    booted.current = true
    void bootstrap()
  }, [bootstrap])

  // PWA: service worker, предложение установки, состояние сети.
  const swBooted = useRef(false)
  useEffect(() => {
    if (!swBooted.current) {
      swBooted.current = true
      registerServiceWorker(() => useAppStore.getState().setUpdateReady(true))
    }
    const stopWatch = watchInstallPrompt((can) => useAppStore.getState().setCanInstall(can))
    const sync = () => useAppStore.getState().setOnline(navigator.onLine)
    window.addEventListener('online', sync)
    window.addEventListener('offline', sync)
    return () => {
      stopWatch()
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', sync)
    }
  }, [])

  // Ярлык «новый чат» с домашнего экрана: /?new=1 создаёт сессию сразу после входа.
  const shortcutDone = useRef(false)
  useEffect(() => {
    if (!authed || shortcutDone.current) return
    const url = new URL(location.href)
    if (url.searchParams.get('new') !== '1') return
    shortcutDone.current = true
    url.searchParams.delete('new')
    history.replaceState(null, '', url.pathname + url.search)
    void useAppStore.getState().newSession()
  }, [authed])

  // Список сессий обновляем при возврате фокуса на вкладку и раз в 15 с.
  useEffect(() => {
    if (!authed) return
    const tick = () => {
      if (document.visibilityState === 'visible') void refreshSessions()
    }
    const timer = window.setInterval(tick, 15000)
    window.addEventListener('focus', tick)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', tick)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [authed, refreshSessions])

  // Мост от панелей: состояние связи и RTT для pill в шапке (FR-13).
  // Панели зовут connSink на каждый ping/pong, здесь сводим их в одно состояние.
  useEffect(() => {
    if (!authed) return
    const conns = new Map<string, { conn: ConnState; rtt: number | null }>()
    setConnSink((sessionId, conn, rtt) => {
      conns.set(sessionId, { conn, rtt })
      const store = useAppStore.getState()
      const rows = store.paneIds
        .map((id) => conns.get(id))
        .filter((row): row is { conn: ConnState; rtt: number | null } => row !== undefined)
      const live = rows.filter((row) => row.conn === 'live')
      let state: LinkState = 'stable'
      let worst: number | null = null
      if (live.length > 0) {
        const times = live.map((row) => row.rtt).filter((value): value is number => value !== null)
        worst = times.length > 0 ? Math.max(...times) : null
        state = worst !== null && worst > 250 ? 'slow' : 'stable'
      } else if (rows.some((row) => row.conn === 'reconnecting' || row.conn === 'connecting')) {
        state = 'reconnecting'
      } else if (rows.length > 0) {
        state = 'offline'
      }
      if (store.linkState !== state || store.rtt !== worst) store.setRtt(worst, state)
    })
    return () => setConnSink(null)
  }, [authed])

  // Дерево и лента обновляются сами: агент пишет заметки мимо интерфейса,
  // и его файлы обязаны появляться без перезагрузки (приёмка 6 контракта).
  // Тянем только когда база на экране — иначе это опрос ради опроса.
  useEffect(() => {
    if (!authed) return
    const watching = section === 'notes' || section === 'feed'
    if (!watching) return
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      void refreshTree()
      void refreshFeed()
    }
    const timer = window.setInterval(tick, 30000)
    window.addEventListener('focus', tick)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', tick)
    }
  }, [authed, section, refreshTree, refreshFeed])

  // Бейдж «Трекер» в сайдбаре закреплён вне свёрнутого дерева и виден из
  // любого раздела (PLAN.md «Трекер сообщений из Telegram») — в отличие от
  // дерева/ленты выше, не привязан к тому, открыта ли «База данных».
  useEffect(() => {
    if (!authed) return
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      void refreshTrackerNew()
    }
    const timer = window.setInterval(tick, 30000)
    window.addEventListener('focus', tick)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', tick)
    }
  }, [authed, refreshTrackerNew])

  if (booting) return <Boot />

  if (!authed) return <Login />

  if (mobile && legacyMobile) return <MobileApp />
  if (uiFlag() === 'kit') {
    return (
      <Suspense fallback={null}>
        <KitDemo />
      </Suspense>
    )
  }

  return <Shell />
}

function uiFlag(): string | null {
  try {
    return new URL(location.href).searchParams.get('ui')
  } catch {
    return null
  }
}

/** Флаг ?ui=legacy-mobile живёт до конца вкладки: навигация внутри старого
 *  MobileApp переписывает адрес, и терять оболочку на первом же переходе нельзя. */
function useLegacyMobile(): boolean {
  const [on] = useState(() => {
    try {
      const flag = new URL(location.href).searchParams.get('ui')
      if (flag === 'legacy-mobile') sessionStorage.setItem('abx-ui', 'legacy-mobile')
      else if (flag) sessionStorage.removeItem('abx-ui')
      return sessionStorage.getItem('abx-ui') === 'legacy-mobile'
    } catch {
      return false
    }
  })
  return on
}
